/**
 * Link attribute policy — single source of truth for the rel/target attributes
 * applied to outbound anchors across the platform.
 *
 * Policy:
 *   - Owned domains (dreamcoachmatch.com, yuriminski.com,
 *     coachingforglobalimpact.com) are internal: dofollow, no forced target,
 *     caller attributes pass through untouched.
 *   - Editorial external links (the default — CIH content, curated citations,
 *     author bylines, CTAs) stay **dofollow**. We link out deliberately, so we
 *     vouch for the destination; that outbound editorial signal helps SEO/AEO
 *     rather than hurting it. They only gain `noopener noreferrer` when opened
 *     in a new tab (security/privacy, not an SEO signal).
 *   - User-generated external links (`ugc: true` — coach-supplied profile
 *     fields) gain `rel="ugc"` so we don't pass equity to unvetted destinations.
 *   - Relative URLs (`/`, `?`, `#`), mailto:, tel:, sms:, javascript: and data:
 *     are not external — the policy leaves them alone.
 *
 * There is intentionally no `nofollow`/`sponsored` handling: we have no paid or
 * affiliate outbound relationships today. Add `sponsored` here (and a matching
 * call-site flag) if/when that changes.
 *
 * Consumed by SafeLink, ButtonLink, BuilderLink, the coach lead-magnet CTA,
 * the SSR ArticleBody/MarkdownContent renderers and the TipTap PolicyLink —
 * anywhere an anchor is emitted from data-supplied hrefs.
 *
 * Aliases: link rel policy, external link attributes, nofollow, ugc, dofollow.
 */

const REL_SPLIT_PATTERN = /\s+/u;
const EXTERNAL_SECURITY_TOKENS = ["noopener", "noreferrer"] as const;
const UGC_TOKEN = "ugc" as const;

const ABSOLUTE_URL_PATTERN = /^(https?:)?\/\//iu;
const SPECIAL_SCHEME_PATTERN = /^(mailto|tel|sms):/iu;
const LEADING_SLASH_PATTERN = /^\/+/u;

/** Domains DCM owns. Anchors pointing here are treated as internal (dofollow). */
export const OWNED_DOMAINS: readonly string[] = [
  "dreamcoachmatch.com",
  "yuriminski.com",
  "coachingforglobalimpact.com",
] as const;

/**
 * Normalizes a coach-supplied external URL so it always renders as an absolute
 * link. Bare hosts like `example.com` or `www.example.com` would otherwise be
 * resolved relative to the current page, producing 404 internal links that SEO
 * crawlers flag.
 */
export const toExternalHref = (raw: string | null | undefined): string => {
  const trimmed = raw?.trim();
  if (!trimmed) return "";
  if (ABSOLUTE_URL_PATTERN.test(trimmed)) return trimmed;
  if (SPECIAL_SCHEME_PATTERN.test(trimmed)) return trimmed;
  return `https://${trimmed.replace(LEADING_SLASH_PATTERN, "")}`;
};

const stripProtocolRelative = (value: string): string =>
  value.startsWith("//") ? `https:${value}` : value;

/**
 * Parses a hostname out of an href. Returns null for relative paths,
 * fragment-only links, mailto:/tel:/sms:/javascript:/data: schemes and
 * malformed URLs.
 */
const safeHostname = (href: string | undefined): string | null => {
  const trimmed = href?.trim();
  if (!trimmed) return null;

  // Protocol-relative URLs (//example.com/...) must be normalized before the
  // relative-path early-out below; otherwise they look relative.
  const startsProtocolRelative = trimmed.startsWith("//");
  if (!startsProtocolRelative && /^[/?#]/u.test(trimmed)) return null;

  const lower = trimmed.toLowerCase();
  if (
    lower.startsWith("mailto:") ||
    lower.startsWith("tel:") ||
    lower.startsWith("sms:") ||
    lower.startsWith("javascript:") ||
    lower.startsWith("data:")
  ) {
    return null;
  }

  try {
    return new URL(stripProtocolRelative(trimmed)).hostname.toLowerCase();
  } catch {
    return null;
  }
};

const isOwnedHost = (host: string): boolean =>
  OWNED_DOMAINS.some(
    (domain) => host === domain || host.endsWith(`.${domain}`),
  );

/**
 * True when the href is an absolute web URL pointing at a domain DCM does not
 * own. Relative URLs, mailto: links, fragment anchors and owned-domain links
 * all return false.
 */
export const isExternalHref = (href: string | undefined): boolean => {
  const host = safeHostname(href);
  return host !== null && !isOwnedHost(host);
};

export interface ResolveLinkOptions {
  /**
   * Mark the destination as user-generated (coach-supplied). Adds `rel="ugc"`
   * for external hrefs. Defaults to false (editorial — dofollow).
   */
  readonly ugc?: boolean;
  /** Caller-supplied rel tokens to preserve and merge with the policy. */
  readonly rel?: string;
  /** Caller-supplied target. External links default to `_blank` when omitted. */
  readonly target?: string;
}

export interface ResolvedLinkAttrs {
  readonly rel?: string;
  readonly target?: string;
}

const buildAttrs = (
  tokens: Set<string>,
  target: string | undefined,
): ResolvedLinkAttrs => ({
  ...(tokens.size > 0 ? { rel: Array.from(tokens).join(" ") } : {}),
  ...(target ? { target } : {}),
});

/**
 * Resolves the final rel/target to spread onto an anchor under the platform
 * link policy. Merges caller-supplied rel tokens with the policy tokens.
 *
 *   <a href={href} {...resolveLinkAttrs(href, { ugc })}>…</a>
 *
 * Internal/owned hrefs return the caller's attributes unchanged (dofollow, no
 * forced target). External hrefs default to `target="_blank"`, gain the
 * security tokens when opened in a new tab, and gain `ugc` when flagged.
 */
export const resolveLinkAttrs = (
  href: string | undefined,
  { ugc = false, rel, target }: ResolveLinkOptions = {},
): ResolvedLinkAttrs => {
  const tokens = new Set<string>();
  for (const token of rel?.split(REL_SPLIT_PATTERN) ?? []) {
    if (token) tokens.add(token);
  }

  if (!isExternalHref(href)) {
    return buildAttrs(tokens, target);
  }

  const resolvedTarget = target ?? "_blank";
  if (ugc) tokens.add(UGC_TOKEN);
  if (resolvedTarget === "_blank") {
    for (const token of EXTERNAL_SECURITY_TOKENS) tokens.add(token);
  }

  return buildAttrs(tokens, resolvedTarget);
};
